import { useCallback, useEffect, useState } from "react";
import { T, F } from "@/lib/bricks";
import { DEMO_MODE } from "@/lib/demoMode";
import {
  deletePasskey,
  formatPasskeyWhen,
  listPasskeys,
  passkeyUserMessage,
  registerPasskey,
  webAuthnAvailable,
} from "@/lib/passkeys";

/** `primary`: this is the page's one main action (Bricks orange). */
export default function PasskeysCard({ p, primary = false }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  // The demo has no real sign-in session, so passkeys can't be listed or added.
  const supported = !DEMO_MODE && webAuthnAvailable();

  const refresh = useCallback(async () => {
    if (DEMO_MODE) { setLoading(false); return; }
    try {
      const next = await listPasskeys();
      setItems(next);
      setError("");
    } catch (err) {
      const msg = passkeyUserMessage(err, "list");
      setError(msg || "Could not load passkeys.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const onAdd = async () => {
    setError("");
    setInfo("");
    setBusy(true);
    try {
      const created = await registerPasskey();
      setInfo(created?.friendly_name ? `Added ${created.friendly_name}.` : "Passkey added.");
      await refresh();
    } catch (err) {
      const msg = passkeyUserMessage(err, "register");
      if (msg) setError(msg);
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async (id) => {
    setError("");
    setInfo("");
    setBusy(true);
    try {
      await deletePasskey(id);
      setItems((prev) => prev.filter((k) => k.id !== id));
    } catch (err) {
      const msg = passkeyUserMessage(err, "register");
      if (msg) setError(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section style={{ color:p.ink, '--bx-pad':'4px', borderBottom:`1px solid ${p.hair}` }}>
      <div style={{ padding:'0 0 14px', borderBottom:`1px solid ${p.hair}`, display:'flex', alignItems:'flex-end', justifyContent:'space-between', gap:16 }}>
        <div>
          <h2 style={{ fontSize:16, fontWeight:500, letterSpacing:-0.2, margin:0, color:p.ink, fontFamily:F.display }}>Passkeys</h2>
          <div style={{ fontSize:12.5, color:p.ink3, marginTop:6 }}>
            Face, fingerprint or a security key. Email, password and Google stay available.
          </div>
        </div>
        <button
          type="button"
          data-testid="passkey-add-btn"
          disabled={busy || !supported}
          onClick={onAdd}
          style={{ padding:'0 16px', minHeight:44, background: primary && supported ? T.brick : 'transparent', color: !supported ? p.ink3 : primary ? T.ink : p.ink, border: primary && supported ? '1px solid transparent' : `1px solid ${supported ? p.hair : 'var(--c-line)'}`, borderRadius:2, fontSize:13, fontFamily:F.display, cursor: supported ? (busy ? 'wait' : 'pointer') : 'not-allowed', whiteSpace:'nowrap' }}
        >
          {busy ? '…' : 'Add passkey'}
        </button>
      </div>
      {!supported && (
        <div style={{ padding:'16px var(--bx-pad)', fontSize:13, color:p.ink3 }}>
          {DEMO_MODE ? 'Passkeys are off in the demo.' : 'This browser does not support passkeys.'}
        </div>
      )}
      {loading && (
        <div style={{ padding:'16px var(--bx-pad)', fontSize:13, color:p.ink3 }}>Loading…</div>
      )}
      {!loading && items.length === 0 && supported && (
        <div style={{ padding:'16px var(--bx-pad)', fontSize:13, color:p.ink3 }}>
          None on this account yet. Add one after you can unlock this device.
        </div>
      )}
      {!loading && items.map((k, i) => (
        <div
          key={k.id}
          style={{
            display:'flex', alignItems:'center', justifyContent:'space-between', gap:16,
            padding:'16px var(--bx-pad)',
            borderTop: (i === 0 && !supported) ? undefined : `1px solid ${p.hair}`,
          }}
        >
          <div>
            <div style={{ fontSize:14 }}>{k.friendly_name || 'Passkey'}</div>
            <div style={{ fontSize:12, color:p.ink3, marginTop:2 }}>
              {[formatPasskeyWhen(k.created_at) && `Added ${formatPasskeyWhen(k.created_at)}`, formatPasskeyWhen(k.last_used_at) && `last used ${formatPasskeyWhen(k.last_used_at)}`].filter(Boolean).join(' · ')}
            </div>
          </div>
          <button
            type="button"
            data-testid="passkey-remove-btn"
            disabled={busy}
            onClick={() => onDelete(k.id)}
            style={{ background:'none', border:'none', cursor:'pointer', color:'var(--c-attn)', fontSize:12, fontFamily:F.display, padding:'0 4px', minHeight:44, minWidth:44 }}
          >
            Remove
          </button>
        </div>
      ))}
      {error && <p style={{ fontSize:13, color:'var(--c-bad)', margin:0, padding:'0 24px 16px' }}>{error}</p>}
      {info && <p style={{ fontSize:13, color:'var(--c-ok)', margin:0, padding:'0 24px 16px' }}>{info}</p>}
    </section>
  );
}
